import type { Game } from '@hive/shared';
import { Link } from 'react-router';
import { useCurrentMember } from '@/auth/member-context';
import { useGames } from '@/data/games';
import { MODULES } from '@/modules';

/** The studio dashboard: the games for now; later phases fill it in. */
export function HomePage() {
  const member = useCurrentMember();
  const games = useGames();
  return (
    <StudioHome
      firstName={member.display_name.split(' ')[0] ?? member.display_name}
      games={(games.data ?? []).filter((g) => !g.is_studio && !g.archived_at)}
    />
  );
}

export function StudioHome({ firstName, games = [] }: { firstName: string; games?: readonly Game[] }) {
  return (
    <div className="mx-auto w-full max-w-[860px] px-7 pt-14 pb-10">
      <p className="hud-label mb-3.5">Studio home</p>
      <h1 className="mb-4 font-display text-3xl leading-tight font-black tracking-[0.04em] uppercase">
        Welcome back, <span className="text-cyan text-shadow-glow-cyan">{firstName}</span>
      </h1>
      <section
        aria-labelledby="games-heading"
        className="cut-tr mt-8 bg-bg-2/85 p-6 shadow-[inset_0_0_0_1px_var(--color-line)]"
      >
        <p className="hud-label">Games</p>
        {games.length === 0 ? (
          <>
            <h2
              id="games-heading"
              className="mt-2 font-display text-lg font-bold tracking-[0.1em] text-magenta uppercase"
            >
              No games yet
            </h2>
            <p className="mt-2 max-w-[62ch] text-muted">
              Studio work lives in{' '}
              <Link to="/grid/studio" className="text-cyan underline">
                Grid
              </Link>
              .{' '}
              <Link to="/games" className="text-cyan underline">
                Add a game
              </Link>{' '}
              and its tasks show up here.
            </p>
          </>
        ) : (
          <>
            <h2 id="games-heading" className="sr-only">
              Games
            </h2>
            <ul className="mt-3 flex flex-col gap-1.5">
              {games.map((g) => (
                <li key={g.id}>
                  <Link to={`/grid/${g.slug}`} className="flex items-baseline gap-3 hover:text-cyan">
                    <span className="font-display text-sm font-bold tracking-[0.1em] uppercase">{g.name}</span>
                    <span className="font-mono text-xs text-muted">{g.status}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
      <ul className="mt-8 grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-2.5">
        {MODULES.map((m) => (
          <li key={m.id}>
            <Link
              to={m.path}
              className="cut-tr block h-full bg-bg-2/85 p-3.5 pb-4 text-sm leading-snug text-muted shadow-[inset_0_0_0_1px_var(--color-line)] transition-shadow hover:shadow-[inset_0_0_0_1px_var(--color-line-hot)]"
            >
              <span className="mb-1.5 block font-display text-xs tracking-[0.16em] text-text uppercase">{m.name}</span>
              {m.summary}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
